import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTheme } from "styled-components";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCoin,
  faCalculator,
  faCheckCircle,
  faCircle,
  faPlusCircle,
  faMinusCircle,
} from "@fortawesome/pro-light-svg-icons";
import WhiteBox from "../../../components/WhiteBox";
import {
  BoldTitle,
  Button,
  Col2Grid,
  FH,
  Input,
  Label,
  Text,
  Title,
} from "../../main-styles";
import {
  getSaldo,
  getExtrato,
  creditoManual,
  salvarRascunho,
  atualizarCalcularLinha,
  simularTodos,
  aprovarCreditos,
  getRascunhoAberto,
  descartarRascunho,
  getCreditos,
} from "../../../api/orgadots";
import { getAllUsers } from "../../../api/user";
import { Spinner } from "reactstrap";
import toast from "react-hot-toast";
import moment from "moment-timezone";
import BackButton from "../../../components/BackButton";
import { useConfirm } from "../../../components/ConfirmProvider";
import {
  ORGADOTS_DEFAULT_PERCENTUAL,
  ORGADOTS_DEFAULT_PERCENTUAL_META,
} from "../../../utils/constants";

/** Aceita vazio ou decimal (vírgula vira ponto); bloqueia letras e símbolos. */
function sanitizeOptionalFloatString(raw) {
  if (raw == null) return "";
  let v = String(raw).replace(",", ".");
  v = v.replace(/[^\d.]/g, "");
  const i = v.indexOf(".");
  if (i !== -1) {
    v = v.slice(0, i + 1) + v.slice(i + 1).replace(/\./g, "");
  }
  return v;
}

function parsePercentNumber(raw) {
  if (raw == null || raw === "") return null;
  const n = parseFloat(String(raw).replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

function stringifyErrPart(x) {
  if (x == null) return "";
  if (typeof x === "string") return x.trim();
  if (typeof x === "number" || typeof x === "boolean") return String(x);
  if (Array.isArray(x)) {
    return x
      .map(stringifyErrPart)
      .filter(Boolean)
      .join(", ");
  }
  if (typeof x === "object") {
    if (x.message != null) return stringifyErrPart(x.message);
    if (x.msg != null) return stringifyErrPart(x.msg);
    try {
      return JSON.stringify(x);
    } catch {
      return "";
    }
  }
  return String(x);
}

function getApiErrorMessage(error, fallback) {
  const status = error?.response?.status;
  const d = error?.response?.data;

  if (process.env.NODE_ENV === "development" && error?.response) {
    // eslint-disable-next-line no-console
    console.error("API error response:", status, d);
  }

  if (d == null || d === "") {
    const base = error?.message || fallback;
    return status ? `${base} (HTTP ${status})` : base;
  }

  if (typeof d === "string") {
    const t = d.trim();
    return t || (status ? `${fallback} (HTTP ${status})` : fallback);
  }

  const parts = [];
  for (const key of [
    "message",
    "msg",
    "erro",
    "error",
    "detail",
    "descricao",
    "description",
    "mensagem",
  ]) {
    if (d[key] != null && d[key] !== "") {
      const s = stringifyErrPart(d[key]);
      if (s) parts.push(s);
    }
  }

  if (Array.isArray(d.errors) && d.errors.length) {
    const s = stringifyErrPart(d.errors);
    if (s) parts.push(s);
  }

  if (d.errors && typeof d.errors === "object" && !Array.isArray(d.errors)) {
    const msgs = Object.entries(d.errors).flatMap(([k, v]) => {
      const msg = stringifyErrPart(v);
      return msg ? [`${k}: ${msg}`] : [];
    });
    if (msgs.length) parts.push(msgs.join("; "));
  }

  if (parts.length) return parts.join(" — ");

  try {
    const raw = JSON.stringify(d);
    if (raw && raw !== "{}") {
      return raw.length > 500 ? `${raw.slice(0, 500)}…` : raw;
    }
  } catch {
    /* ignore */
  }

  return status ? `${fallback} (HTTP ${status}, sem detalhe no corpo)` : fallback;
}

function dateOnly(v) {
  if (v == null || v === "") return "";
  return String(v).split("T")[0];
}

function periodoConfigKey(cg) {
  return `${dateOnly(cg.data_inicio)}|${dateOnly(cg.data_fim)}|${String(
    cg.tipo_divisao || "pet"
  ).toLowerCase()}`;
}

/**
 * Linha efetiva para salvar: cada distribuidor carrega sempre valores concretos.
 * Exceção usa os campos custom (com fallback para a config geral); sem exceção,
 * usa a config geral. Datas sempre concretas.
 */
function buildLinhaEfetiva(d, configGeral) {
  const useExcecao = !!d.excecao;
  const pct =
    parsePercentNumber(
      useExcecao && d.customPercentual ? d.customPercentual : configGeral.percentual
    ) ?? parsePercentNumber(configGeral.percentual);
  const meta =
    parsePercentNumber(
      useExcecao && d.customPercentualMeta
        ? d.customPercentualMeta
        : configGeral.percentual_meta
    ) ?? 0;
  const dataInicio = dateOnly(
    useExcecao && d.customDataInicio ? d.customDataInicio : configGeral.data_inicio
  );
  const dataFim = dateOnly(
    useExcecao && d.customDataFim ? d.customDataFim : configGeral.data_fim
  );

  return {
    id_distribuidor: Number(d.id),
    percentual: pct,
    percentual_meta: meta,
    data_inicio: dataInicio,
    data_fim: dataFim,
  };
}

function normalizeCreditosList(data) {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.rows)) return data.rows;
  if (data && Array.isArray(data.data)) return data.data;
  return [];
}

function normalizeHistoricoList(data) {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.data)) return data.data;
  if (data && Array.isArray(data.items)) return data.items;
  return [];
}

/**
 * Salvar o rascunho gera N linhas no banco (1 por distribuidor).
 * No histórico, agrupamos por período + divisão + status = 1 linha = 1 lote lógico.
 */
function agruparHistoricoCreditos(rows) {
  if (!Array.isArray(rows) || !rows.length) return [];
  const map = new Map();
  for (const h of rows) {
    const di = dateOnly(h.data_inicio);
    const df = dateOnly(h.data_fim);
    const tipo = String(h.tipo_divisao || "pet").toLowerCase();
    const st = String(h.status || "").toLowerCase();
    const key = `${di}|${df}|${tipo}|${st}`;
    if (!map.has(key)) {
      map.set(key, {
        key,
        data_inicio: h.data_inicio,
        data_fim: h.data_fim,
        tipo_divisao: h.tipo_divisao,
        status: h.status,
        ids: [],
        created_at: h.created_at,
      });
    }
    const g = map.get(key);
    g.ids.push(h.id);
    const t1 = new Date(h.created_at).getTime();
    const t2 = new Date(g.created_at).getTime();
    if (t1 < t2) g.created_at = h.created_at;
  }
  return Array.from(map.values())
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .map((g) => ({
      ...g,
      quantidadeDistribuidores: g.ids.length,
    }));
}

/** Valor mais frequente (moda) de uma lista; ignora vazios. */
function modeOf(values) {
  const counts = new Map();
  let best = null;
  let bestN = 0;
  for (const v of values) {
    if (v == null || v === "") continue;
    const k = String(v);
    const n = (counts.get(k) || 0) + 1;
    counts.set(k, n);
    if (n > bestN) {
      bestN = n;
      best = v;
    }
  }
  return best;
}

function hydrateRascunhoFromApi(full, allUsers, setters) {
  const {
    setCreditoIds,
    setCreditoStatus,
    setConfigGeral,
    setDistribuidores,
  } = setters;

  const root = full.credito || full.data || full;
  const cfg = root.config || full.config || {};
  let linhas =
    full.distribuidores ||
    full.linhas ||
    root.distribuidores ||
    root.linhas ||
    [];
  /* GET /creditos/:id devolve uma linha de orgadots_creditos, não um array "distribuidores" */
  if (!linhas.length && root.id_distribuidor != null) {
    linhas = [{ ...root, id_distribuidor: root.id_distribuidor }];
  }

  // A "Configuração Geral" não existe no servidor: a base é o valor mais comum
  // entre as linhas (a maioria sem exceção). Linhas que divergem viram exceção.
  const tipo = cfg.tipo_divisao || root.tipo_divisao || "pet";
  const dataInicio =
    dateOnly(
      modeOf(linhas.map((l) => dateOnly(l.data_inicio))) ||
        cfg.data_inicio ||
        root.data_inicio
    ) || moment().subtract(1, "month").startOf("month").format("YYYY-MM-DD");
  const dataFim =
    dateOnly(
      modeOf(linhas.map((l) => dateOnly(l.data_fim))) ||
        cfg.data_fim ||
        root.data_fim
    ) || moment().subtract(1, "month").endOf("month").format("YYYY-MM-DD");

  const pctRaw = modeOf(linhas.map((l) => l.percentual)) ?? cfg.percentual ?? root.percentual;
  const pctMetaRaw =
    modeOf(linhas.map((l) => l.percentual_meta)) ?? cfg.percentual_meta ?? root.percentual_meta;

  const pct =
    pctRaw != null && pctRaw !== ""
      ? sanitizeOptionalFloatString(String(pctRaw))
      : ORGADOTS_DEFAULT_PERCENTUAL;
  const pctMeta =
    pctMetaRaw != null && pctMetaRaw !== ""
      ? sanitizeOptionalFloatString(String(pctMetaRaw))
      : ORGADOTS_DEFAULT_PERCENTUAL_META;

  const idsFromLinhas = linhas
    .map((l) => l.id)
    .filter((x) => x != null && x !== "");
  if (idsFromLinhas.length) {
    setCreditoIds(idsFromLinhas);
  } else if (root.id != null) {
    setCreditoIds([root.id]);
  } else {
    setCreditoIds([]);
  }
  const st = String(root.status || "rascunho").toLowerCase();
  setCreditoStatus(st === "draft" ? "rascunho" : st);

  setConfigGeral({
    data_inicio: dataInicio,
    data_fim: dataFim,
    percentual: pct,
    percentual_meta: pctMeta,
    tipo_divisao: tipo,
  });

  const gPct = pct;
  const gMeta = pctMeta;

  const filtered = allUsers.filter((u) => u.tipo === tipo || !u.tipo);

  setDistribuidores(
    filtered.map((u) => {
      const id = parseInt(u.id, 10);
      const linha = linhas.find(
        (l) => parseInt(l.id_distribuidor, 10) === id
      );

      if (!linha) {
        return {
          id: u.id,
          nome: u.full_name || u.name,
          cnpj: u.cnpj || "",
          enabled: false,
          customPercentual: "",
          customPercentualMeta: "",
          customDataInicio: "",
          customDataFim: "",
          valorCalculado: null,
          excecao: false,
          idCreditoLinha: null,
        };
      }

      const lPct =
        linha.percentual != null && linha.percentual !== ""
          ? sanitizeOptionalFloatString(String(linha.percentual))
          : "";
      const lMeta =
        linha.percentual_meta != null && linha.percentual_meta !== ""
          ? sanitizeOptionalFloatString(String(linha.percentual_meta))
          : "";
      const ldIni = dateOnly(linha.data_inicio);
      const ldFim = dateOnly(linha.data_fim);

      const pctDiff = lPct !== "" && lPct !== gPct;
      const metaDiff = lMeta !== "" && lMeta !== gMeta;
      const dataDiff =
        (ldIni && ldIni !== dataInicio) || (ldFim && ldFim !== dataFim);
      const excecaoFlag =
        linha.excecao === true ||
        linha.is_excecao === true ||
        pctDiff ||
        metaDiff ||
        dataDiff;

      const enabled =
        linha.enabled !== false &&
        linha.incluso !== false &&
        linha.ativo !== false;

      return {
        id: u.id,
        nome: u.full_name || u.name,
        cnpj: u.cnpj || "",
        enabled,
        customPercentual: pctDiff ? lPct : "",
        customPercentualMeta: metaDiff ? lMeta : "",
        customDataInicio:
          dataDiff && ldIni && ldIni !== dataInicio ? ldIni : "",
        customDataFim: dataDiff && ldFim && ldFim !== dataFim ? ldFim : "",
        valorCalculado:
          linha.valor_calculado != null
            ? linha.valor_calculado
            : linha.valorCalculado != null
            ? linha.valorCalculado
            : null,
        excecao: !!excecaoFlag,
        idCreditoLinha: linha.id != null ? linha.id : null,
      };
    })
  );
}

function OrgaDots() {
  const theme = useTheme();
  const confirm = useConfirm();
  const { user } = useSelector((state) => state.authReducer);
  const isAdmin = user?.roles?.includes("admin");

  const [tab, setTab] = useState("credito"); // credito | manual | extrato
  const [loading, setLoading] = useState(false);

  // Credito mensal state — o backend cria 1 registro em orgadots_creditos por distribuidor (array na resposta)
  const [creditoIds, setCreditoIds] = useState([]);
  const [creditoStatus, setCreditoStatus] = useState("rascunho");
  const [configGeral, setConfigGeral] = useState({
    data_inicio: moment().subtract(1, "month").startOf("month").format("YYYY-MM-DD"),
    data_fim: moment().subtract(1, "month").endOf("month").format("YYYY-MM-DD"),
    percentual: ORGADOTS_DEFAULT_PERCENTUAL,
    percentual_meta: ORGADOTS_DEFAULT_PERCENTUAL_META,
    tipo_divisao: user?.tipo || "pet",
  });
  const [distribuidores, setDistribuidores] = useState([]);
  const [historico, setHistorico] = useState([]);
  const [historicoReady, setHistoricoReady] = useState(false);
  /** Só créditos aprovados (meses anteriores), para a tabela de baixo */
  const [historicoAprovadosAgrupado, setHistoricoAprovadosAgrupado] = useState(
    []
  );

  const lastApplySigRef = useRef("");

  // Manual credit state
  const [manualForm, setManualForm] = useState({
    id_user: "",
    tipo: "credito",
    valor: "",
    descricao: "",
  });

  // Extrato state
  const [extratoUserId, setExtratoUserId] = useState("");
  const [extrato, setExtrato] = useState([]);
  const [saldo, setSaldo] = useState(null);
  const [allUsers, setAllUsers] = useState([]);

  useEffect(() => {
    _loadUsers();
  }, []);

  useEffect(() => {
    _loadRascunho(configGeral.tipo_divisao);
  }, [configGeral.tipo_divisao]);

  useEffect(() => {
    _loadHistoricoAprovados();
  }, []);

  useEffect(() => {
    lastApplySigRef.current = "";
  }, [configGeral.tipo_divisao]);

  async function _loadUsers() {
    try {
      const users = await getAllUsers(1, 10000);
      setAllUsers(users.filter((u) => u.roles?.includes("distribuidor")));
    } catch (e) {
      console.error("Error loading users:", e);
    }
  }

  async function _loadRascunho(tipo) {
    try {
      const data = await getRascunhoAberto(tipo ?? configGeral.tipo_divisao);
      setHistorico(normalizeHistoricoList(data));
    } catch (e) {
      console.error("Error loading rascunho:", e);
      setHistorico([]);
    } finally {
      setHistoricoReady(true);
    }
  }

  async function _loadHistoricoAprovados() {
    try {
      const data = await getCreditos(1, 200);
      const rows = normalizeCreditosList(data);
      const aprovados = rows.filter(
        (x) => String(x.status || "").toLowerCase() === "aprovado"
      );
      setHistoricoAprovadosAgrupado(agruparHistoricoCreditos(aprovados));
    } catch (e) {
      console.error("Error loading historico aprovados:", e);
      setHistoricoAprovadosAgrupado([]);
    }
  }

  // ===== CREDITO MENSAL =====
  async function handleSalvarRascunho() {
    if (!configGeral.percentual) {
      toast.error("Preencha o percentual");
      return;
    }
    const pctGeral = parsePercentNumber(configGeral.percentual);
    if (pctGeral == null) {
      toast.error("Percentual inválido");
      return;
    }

    const enabledList = distribuidores.filter((d) => d.enabled);
    if (!enabledList.length) {
      toast.error("Marque pelo menos um distribuidor");
      return;
    }

    setLoading(true);
    try {
      const linhas = enabledList.map((d) => buildLinhaEfetiva(d, configGeral));
      await salvarRascunho(configGeral.tipo_divisao, linhas);
      lastApplySigRef.current = "";
      toast.success("Rascunho salvo!");
      await _loadRascunho(configGeral.tipo_divisao);
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Erro ao salvar rascunho"));
    }
    setLoading(false);
  }

  async function handleCalcularLinha(distrib) {
    const idLinha = distrib.idCreditoLinha;
    if (!idLinha) {
      toast.error("Salve o rascunho primeiro");
      return;
    }
    // Persiste só os valores desta linha (datas/percentuais/exceção) e recalcula
    // apenas ela — sem tocar nas outras linhas nem re-hidratar o formulário.
    const valores = buildLinhaEfetiva(distrib, configGeral);
    setLoading(true);
    try {
      const result = await atualizarCalcularLinha(idLinha, {
        percentual: valores.percentual,
        percentual_meta: valores.percentual_meta,
        data_inicio: valores.data_inicio,
        data_fim: valores.data_fim,
      });
      setDistribuidores((prev) =>
        prev.map((d) =>
          d.id === distrib.id
            ? { ...d, valorCalculado: result.valor_calculado }
            : d
        )
      );
      toast.success(`Calculado: ${result.valor_calculado} OrgaDots`);
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Erro ao calcular"));
    }
    setLoading(false);
  }

  async function handleSimular() {
    const pctGeral = parsePercentNumber(configGeral.percentual);
    if (pctGeral == null) {
      toast.error("Percentual inválido");
      return;
    }
    const enabledList = distribuidores.filter((d) => d.enabled);
    if (!enabledList.length) {
      toast.error("Marque pelo menos um distribuidor");
      return;
    }
    setLoading(true);
    try {
      // Salva o formulário atual (datas/percentuais/exceções) ANTES de simular.
      // Assim o cálculo usa sempre o que está na tela e, se a API falhar, as
      // edições não se perdem na re-hidratação (antes o form "zerava").
      const linhas = enabledList.map((d) => buildLinhaEfetiva(d, configGeral));
      const savedRows = normalizeHistoricoList(
        await salvarRascunho(configGeral.tipo_divisao, linhas)
      );
      // ids salvos casados por distribuidor (o id da linha muda ao salvar)
      const idPorDist = new Map(
        savedRows.map((r) => [Number(r.id_distribuidor), Number(r.id)])
      );
      const distPorCredito = new Map(
        savedRows.map((r) => [Number(r.id), Number(r.id_distribuidor)])
      );
      const ids = enabledList
        .map((d) => idPorDist.get(Number(d.id)))
        .filter((x) => Number.isFinite(x));
      if (!ids.length) {
        toast.error("Nada para simular");
        setLoading(false);
        return;
      }
      const results = await simularTodos(ids);
      if (results) {
        setDistribuidores((prev) =>
          prev.map((d) => {
            const result = results.find(
              (r) => distPorCredito.get(Number(r.id)) === Number(d.id)
            );
            if (!result) return d;
            if (result.error) return { ...d, valorCalculado: null };
            return { ...d, valorCalculado: result.valor_calculado };
          })
        );
      }
      setCreditoStatus("simulado");
      const erros = (results || []).filter((r) => r && r.error);
      if (erros.length) {
        const nomeDe = (r) => {
          const distId = distPorCredito.get(Number(r.id));
          const d = distribuidores.find((x) => Number(x.id) === distId);
          return d?.nome || d?.cnpj || `#${r.id}`;
        };
        // Mostra a mensagem real da API por distribuidor. O 404 costuma ser
        // "sem movimento no período" (não falta de cadastro); deixamos uma dica.
        for (const r of erros) {
          const base = r.message || "erro ao calcular na API";
          const dica =
            Number(r.status) === 404
              ? " (verifique se há movimento desse distribuidor no período)"
              : "";
          toast.error(`${nomeDe(r)}: ${base}${dica}`);
        }
      } else {
        toast.success("Simulação concluída!");
      }
      lastApplySigRef.current = "";
      await _loadRascunho(configGeral.tipo_divisao);
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Erro na simulação"));
    }
    setLoading(false);
  }

  async function handleAprovar() {
    const ids = distribuidores
      .filter((d) => d.enabled && d.idCreditoLinha)
      .map((d) => d.idCreditoLinha);
    if (!ids.length) return;
    if (
      !(await confirm({
        title: "Aprovar créditos",
        message:
          "Tem certeza que deseja aprovar? Os créditos serão lançados imediatamente.",
        confirmText: "Aprovar",
      }))
    )
      return;
    setLoading(true);
    try {
      await aprovarCreditos(ids);
      setCreditoStatus("aprovado");
      setCreditoIds([]);
      setDistribuidores((prev) =>
        prev.map((d) => ({
          ...d,
          idCreditoLinha: null,
          valorCalculado: null,
        }))
      );
      toast.success("Créditos aprovados e lançados!");
      lastApplySigRef.current = "";
      await _loadRascunho(configGeral.tipo_divisao);
      _loadHistoricoAprovados();
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Erro ao aprovar"));
    }
    setLoading(false);
  }

  /** `cg` opcional: use ao resetar a tela para não depender do `configGeral` ainda não commitado no estado. */
  function initDistribuidores(cg) {
    const cfg = cg ?? configGeral;
    const filtered = allUsers.filter(
      (u) => u.tipo === cfg.tipo_divisao || !u.tipo
    );
    setCreditoIds([]);
    setDistribuidores(
      filtered.map((u) => ({
        id: u.id,
        nome: u.full_name || u.name,
        cnpj: u.cnpj || "",
        enabled: true,
        customPercentual: "",
        customPercentualMeta: "",
        customDataInicio: "",
        customDataFim: "",
        valorCalculado: null,
        excecao: false,
        idCreditoLinha: null,
      }))
    );
  }

  /** Hidrata o formulário com todas as linhas do rascunho/simulado do período + divisão (1 GET por período). */
  useEffect(() => {
    if (!allUsers.length || !historicoReady) return;

    const tipoWanted = String(configGeral.tipo_divisao || "pet").toLowerCase();
    const lista = normalizeHistoricoList(historico);
    const listaTipo = lista.filter(
      (x) =>
        String(x.tipo_divisao || "pet").toLowerCase() === tipoWanted
    );
    const abertos = listaTipo.filter((x) =>
      ["rascunho", "simulado", "draft"].includes(
        String(x.status || "").toLowerCase()
      )
    );

    const sig = `${periodoConfigKey(configGeral)}|${abertos
      .map((x) => `${x.id}:${String(x.status || "").toLowerCase()}`)
      .sort()
      .join(",")}`;

    if (sig === lastApplySigRef.current) return;

    if (abertos.length) {
      hydrateRascunhoFromApi(
        {
          ...abertos[0],
          distribuidores: abertos.map((row) => ({
            id: row.id,
            id_distribuidor: row.id_distribuidor,
            percentual: row.percentual,
            percentual_meta: row.percentual_meta,
            data_inicio: row.data_inicio,
            data_fim: row.data_fim,
            valor_calculado: row.valor_calculado,
            status: row.status,
          })),
        },
        allUsers,
        {
          setCreditoIds,
          setCreditoStatus,
          setConfigGeral,
          setDistribuidores,
        }
      );
      const anySimulado = abertos.some(
        (x) => String(x.status || "").toLowerCase() === "simulado"
      );
      setCreditoStatus(anySimulado ? "simulado" : "rascunho");
      lastApplySigRef.current = sig;
      return;
    }

    if (creditoIds.length > 0) {
      return;
    }

    initDistribuidores();
    setCreditoStatus("rascunho");
    lastApplySigRef.current = sig;
  }, [
    allUsers,
    historico,
    historicoReady,
    configGeral.tipo_divisao,
    creditoIds.length,
  ]);

  // ===== MANUAL =====
  async function handleCreditoManual() {
    if (!manualForm.id_user || !manualForm.valor || !manualForm.descricao) {
      toast.error("Preencha todos os campos (usuário, valor e descrição/motivo)");
      return;
    }
    setLoading(true);
    try {
      await creditoManual(
        parseInt(manualForm.id_user),
        manualForm.tipo,
        parseFloat(manualForm.valor),
        manualForm.descricao
      );
      toast.success(
        manualForm.tipo === "credito"
          ? "Crédito manual lançado!"
          : "Débito manual lançado!"
      );
      setManualForm({ id_user: "", tipo: "credito", valor: "", descricao: "" });
    } catch (e) {
      toast.error(e.message || "Erro ao lançar crédito/débito manual");
    }
    setLoading(false);
  }

  // ===== EXTRATO =====
  async function handleLoadExtrato() {
    if (!extratoUserId) {
      toast.error("Selecione um distribuidor");
      return;
    }
    setLoading(true);
    try {
      const [saldoData, extratoData] = await Promise.all([
        getSaldo(extratoUserId),
        getExtrato(extratoUserId),
      ]);
      setSaldo(saldoData.saldo || 0);
      setExtrato(extratoData?.rows || []);
    } catch (e) {
      toast.error("Erro ao carregar extrato");
    }
    setLoading(false);
  }

  async function handleDescartarRascunhoAtual() {
    if (!creditoIds.length) {
      toast.error("Não há rascunho para descartar.");
      return;
    }
    if (
      !(await confirm({
        title: "Descartar rascunho",
        message:
          "Descartar o rascunho? Os registros em aberto desta divisão serão excluídos.",
        confirmText: "Descartar",
        danger: true,
      }))
    ) {
      return;
    }
    setLoading(true);
    try {
      await descartarRascunho(configGeral.tipo_divisao);
      toast.success("Rascunho descartado.");
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Erro ao descartar"));
    } finally {
      setCreditoStatus("rascunho");
      lastApplySigRef.current = "";
      initDistribuidores();
      await _loadRascunho(configGeral.tipo_divisao);
      setLoading(false);
    }
  }

  function handleTipoDivisaoChange(nextTipo) {
    setConfigGeral((s) => ({ ...s, tipo_divisao: nextTipo }));
    setCreditoIds([]);
    setCreditoStatus("rascunho");
    lastApplySigRef.current = "";
  }

  return (
    <div style={{ marginTop: 20 }}>
      <BackButton />
      <BoldTitle>OrgaDots - Gestão de Créditos</BoldTitle>

      <FH style={{ gap: 10, marginBottom: 20, marginTop: 10 }}>
        <TabButton active={tab === "credito"} onClick={() => setTab("credito")} theme={theme}>
          Crédito Mensal
        </TabButton>
        <TabButton active={tab === "manual"} onClick={() => setTab("manual")} theme={theme}>
          Crédito/Débito Manual
        </TabButton>
        <TabButton active={tab === "extrato"} onClick={() => setTab("extrato")} theme={theme}>
          Consultar Extrato
        </TabButton>
      </FH>

      {tab === "credito" && (
        <CreditoMensal
          configGeral={configGeral}
          setConfigGeral={setConfigGeral}
          distribuidores={distribuidores}
          setDistribuidores={setDistribuidores}
          temCreditoRascunho={creditoIds.length > 0}
          creditoStatus={creditoStatus}
          loading={loading}
          onSalvarRascunho={handleSalvarRascunho}
          onCalcularLinha={handleCalcularLinha}
          onSimular={handleSimular}
          onAprovar={handleAprovar}
          historicoAprovadosAgrupado={historicoAprovadosAgrupado}
          onDescartarRascunho={handleDescartarRascunhoAtual}
          onTipoDivisaoChange={handleTipoDivisaoChange}
          theme={theme}
          isAdmin={isAdmin}
        />
      )}

      {tab === "manual" && (
        <ManualCredit
          form={manualForm}
          setForm={setManualForm}
          allUsers={allUsers}
          loading={loading}
          onSubmit={handleCreditoManual}
          theme={theme}
        />
      )}

      {tab === "extrato" && (
        <ExtratoConsulta
          allUsers={allUsers}
          extratoUserId={extratoUserId}
          setExtratoUserId={setExtratoUserId}
          extrato={extrato}
          saldo={saldo}
          loading={loading}
          onLoad={handleLoadExtrato}
          theme={theme}
        />
      )}
    </div>
  );
}

function TabButton({ active, onClick, children, theme }) {
  return (
    <div
      onClick={onClick}
      style={{
        padding: "8px 16px",
        borderRadius: 20,
        backgroundColor: active ? theme.primary : "#f0f0f0",
        color: active ? "#fff" : "#333",
        cursor: "pointer",
        fontSize: 13,
        fontWeight: 600,
      }}
    >
      {children}
    </div>
  );
}

// ===== CREDITO MENSAL COMPONENT =====
function CreditoMensal({
  configGeral,
  setConfigGeral,
  distribuidores,
  setDistribuidores,
  temCreditoRascunho,
  creditoStatus,
  loading,
  onSalvarRascunho,
  onCalcularLinha,
  onSimular,
  onAprovar,
  historicoAprovadosAgrupado,
  onDescartarRascunho,
  onTipoDivisaoChange,
  theme,
  isAdmin,
}) {
  const distribuidoresDisplay = useMemo(
    () =>
      [...distribuidores].sort((a, b) =>
        String(a.nome || "").localeCompare(String(b.nome || ""))
      ),
    [distribuidores]
  );

  const linhasComCredito = distribuidores.filter((d) => d.idCreditoLinha).length;

  return (
    <>
      <WhiteBox style={{ marginBottom: 20 }}>
        <Title>Configuração Geral</Title>
        <Col2Grid style={{ gap: 10 }}>
          <div>
            <Label>Período - Início</Label>
            <Input
              type="date"
              value={configGeral.data_inicio}
              onChange={(e) =>
                setConfigGeral((s) => ({ ...s, data_inicio: e.target.value }))
              }
            />
          </div>
          <div>
            <Label>Período - Fim</Label>
            <Input
              type="date"
              value={configGeral.data_fim}
              onChange={(e) =>
                setConfigGeral((s) => ({ ...s, data_fim: e.target.value }))
              }
            />
          </div>
          <div>
            <Label>Percentual (%)</Label>
            <Input
              type="text"
              inputMode="decimal"
              autoComplete="off"
              value={configGeral.percentual}
              onChange={(e) =>
                setConfigGeral((s) => ({
                  ...s,
                  percentual: sanitizeOptionalFloatString(e.target.value),
                }))
              }
              placeholder="Ex: 5"
            />
          </div>
          <div>
            <Label>Percentual Meta (%)</Label>
            <Input
              type="text"
              inputMode="decimal"
              autoComplete="off"
              value={configGeral.percentual_meta}
              onChange={(e) =>
                setConfigGeral((s) => ({
                  ...s,
                  percentual_meta: sanitizeOptionalFloatString(e.target.value),
                }))
              }
              placeholder="Opcional"
            />
          </div>
          {isAdmin && (
            <div>
              <Label>Divisão</Label>
              <select
                value={configGeral.tipo_divisao}
                onChange={(e) => onTipoDivisaoChange(e.target.value)}
                style={{
                  padding: 10,
                  borderRadius: 4,
                  border: "1px solid #ccc",
                  width: "100%",
                }}
              >
                <option value="pet">PET</option>
                <option value="equi">EQUI</option>
              </select>
            </div>
          )}
        </Col2Grid>
      </WhiteBox>

      <WhiteBox style={{ marginBottom: 20 }}>
        <FH style={{ justifyContent: "space-between", alignItems: "center" }}>
          <Title>Distribuidores ({distribuidores.filter((d) => d.enabled).length})</Title>
          <FH style={{ gap: 10 }}>
            {creditoStatus !== "aprovado" && (
              <Button
                style={{ width: "auto", fontSize: 13 }}
                onClick={onSalvarRascunho}
                disabled={loading}
              >
                {loading ? <Spinner size="sm" /> : "Salvar rascunho"}
              </Button>
            )}
            {temCreditoRascunho && creditoStatus !== "aprovado" && (
              <>
                <Button
                  style={{ width: "auto", fontSize: 13, backgroundColor: "#2196f3" }}
                  onClick={onSimular}
                  disabled={loading}
                >
                  {loading ? <Spinner size="sm" /> : "Simular Todos"}
                </Button>
                <Button
                  style={{ width: "auto", fontSize: 13, backgroundColor: "#4caf50" }}
                  onClick={onAprovar}
                  disabled={loading}
                >
                  Aprovar e Creditar
                </Button>
              </>
            )}
            {creditoStatus === "aprovado" && (
              <span style={{ color: "#4caf50", fontWeight: 600 }}>
                <FontAwesomeIcon icon={faCheckCircle} /> Aprovado
              </span>
            )}
          </FH>
        </FH>

        <table
          className="table table-sm align-middle"
          style={{ marginTop: 10, fontSize: 13 }}
        >
          <thead>
            <tr>
              <th></th>
              <th>Distribuidor</th>
              <th>CNPJ</th>
              <th>% Percentual</th>
              <th>% Meta</th>
              <th>Início (exceção)</th>
              <th>Fim (exceção)</th>
              <th>Valor Calculado</th>
              <th style={{ textAlign: "right" }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {distribuidoresDisplay.map((d) => (
              <tr
                key={d.id}
                style={{
                  opacity: d.enabled ? 1 : 0.5,
                  backgroundColor: d.excecao ? "#fffef6" : "transparent",
                }}
              >
                <td>
                  <FontAwesomeIcon
                    icon={d.enabled ? faCheckCircle : faCircle}
                    style={{ cursor: "pointer", color: d.enabled ? "#4caf50" : "#ccc" }}
                    onClick={() =>
                      setDistribuidores((prev) =>
                        prev.map((x) =>
                          x.id === d.id ? { ...x, enabled: !x.enabled } : x
                        )
                      )
                    }
                  />
                </td>
                <td>{d.nome}</td>
                <td>{d.cnpj || "-"}</td>
                <td>
                  {d.excecao ? (
                    <Input
                      type="text"
                      inputMode="decimal"
                      autoComplete="off"
                      style={{ width: 70, marginBottom: 0, padding: 4 }}
                      value={
                        d.customPercentual || configGeral.percentual || ""
                      }
                      onChange={(e) =>
                        setDistribuidores((prev) =>
                          prev.map((x) =>
                            x.id === d.id
                              ? {
                                  ...x,
                                  customPercentual: sanitizeOptionalFloatString(
                                    e.target.value
                                  ),
                                }
                              : x
                          )
                        )
                      }
                    />
                  ) : (
                    configGeral.percentual || "-"
                  )}
                </td>
                <td>
                  {d.excecao ? (
                    <Input
                      type="text"
                      inputMode="decimal"
                      autoComplete="off"
                      style={{ width: 70, marginBottom: 0, padding: 4 }}
                      value={
                        d.customPercentualMeta ||
                        configGeral.percentual_meta ||
                        ""
                      }
                      onChange={(e) =>
                        setDistribuidores((prev) =>
                          prev.map((x) =>
                            x.id === d.id
                              ? {
                                  ...x,
                                  customPercentualMeta:
                                    sanitizeOptionalFloatString(e.target.value),
                                }
                              : x
                          )
                        )
                      }
                    />
                  ) : (
                    configGeral.percentual_meta || "-"
                  )}
                </td>
                <td>
                  {d.excecao ? (
                    <Input
                      type="date"
                      style={{ width: 130, marginBottom: 0, padding: 4 }}
                      value={
                        d.customDataInicio || configGeral.data_inicio || ""
                      }
                      onChange={(e) =>
                        setDistribuidores((prev) =>
                          prev.map((x) =>
                            x.id === d.id
                              ? { ...x, customDataInicio: e.target.value }
                              : x
                          )
                        )
                      }
                    />
                  ) : (
                    <span style={{ color: "#999" }}>—</span>
                  )}
                </td>
                <td>
                  {d.excecao ? (
                    <Input
                      type="date"
                      style={{ width: 130, marginBottom: 0, padding: 4 }}
                      value={d.customDataFim || configGeral.data_fim || ""}
                      onChange={(e) =>
                        setDistribuidores((prev) =>
                          prev.map((x) =>
                            x.id === d.id
                              ? { ...x, customDataFim: e.target.value }
                              : x
                          )
                        )
                      }
                    />
                  ) : (
                    <span style={{ color: "#999" }}>—</span>
                  )}
                </td>
                <td>
                  {d.valorCalculado !== null && d.valorCalculado !== undefined ? (
                    <span style={{ fontWeight: 600, color: theme.primary }}>
                      <FontAwesomeIcon icon={faCoin} /> {d.valorCalculado}
                    </span>
                  ) : (
                    "-"
                  )}
                </td>
                <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                  <span
                    style={{
                      cursor: "pointer",
                      marginRight: 8,
                      color: d.excecao ? "#f57c00" : "#999",
                      fontSize: 12,
                    }}
                    onClick={() =>
                      setDistribuidores((prev) =>
                        prev.map((x) => {
                          if (x.id !== d.id) return x;
                          const nextExcecao = !x.excecao;
                          if (nextExcecao) {
                            // ligar: pré-preenche com a config geral pra editar a partir da base real
                            return {
                              ...x,
                              excecao: true,
                              customPercentual: configGeral.percentual || "",
                              customPercentualMeta: configGeral.percentual_meta || "",
                              customDataInicio: configGeral.data_inicio || "",
                              customDataFim: configGeral.data_fim || "",
                            };
                          }
                          // desligar: volta a herdar a config geral
                          return {
                            ...x,
                            excecao: false,
                            customPercentual: "",
                            customPercentualMeta: "",
                            customDataInicio: "",
                            customDataFim: "",
                          };
                        })
                      )
                    }
                  >
                    {d.excecao ? "Remover Exceção" : "Exceção"}
                  </span>
                  {d.idCreditoLinha && d.enabled && (
                    <span
                      onClick={() => onCalcularLinha(d)}
                      title="Salva os valores desta linha e recalcula só ela"
                      style={{
                        cursor: "pointer",
                        color: "#2196f3",
                        fontSize: 12,
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                      }}
                    >
                      <FontAwesomeIcon icon={faCalculator} /> Atualizar linha
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </WhiteBox>

      {temCreditoRascunho && creditoStatus !== "aprovado" && (
        <WhiteBox style={{ marginBottom: 20 }}>
          <FH
            style={{
              justifyContent: "space-between",
              alignItems: "flex-start",
              flexWrap: "wrap",
              gap: 12,
            }}
          >
            <div>
              <Title style={{ marginBottom: 4 }}>Rascunho atual</Title>
              <Text style={{ color: "#666", fontSize: 13, marginBottom: 0 }}>
                Período{" "}
                {moment(configGeral.data_inicio).format("DD/MM/YYYY")} a{" "}
                {moment(configGeral.data_fim).format("DD/MM/YYYY")} · Divisão{" "}
                {String(configGeral.tipo_divisao).toUpperCase()} · Status:{" "}
                <strong>{creditoStatus}</strong> · {linhasComCredito}{" "}
                registro(s) salvos no servidor para este lançamento.
              </Text>
            </div>
            <Button
              style={{
                width: "auto",
                fontSize: 13,
                backgroundColor: "#c62828",
                color: "#fff",
              }}
              onClick={onDescartarRascunho}
              disabled={loading}
            >
              Descartar rascunho
            </Button>
          </FH>
        </WhiteBox>
      )}

      {historicoAprovadosAgrupado.length > 0 && (
        <WhiteBox>
          <Title>Histórico — créditos aprovados</Title>
          <Text style={{ color: "#666", fontSize: 12, marginBottom: 8 }}>
            Lotes já lançados (consulta). O rascunho em edição aparece no bloco
            acima até você aprovar.
          </Text>
          <table
            className="table table-sm align-middle"
            style={{ fontSize: 13, marginTop: 10 }}
          >
            <thead>
              <tr>
                <th>Distribuidores</th>
                <th>Divisão</th>
                <th>Período</th>
                <th>Criado em</th>
              </tr>
            </thead>
            <tbody>
              {historicoAprovadosAgrupado.map((g) => (
                <tr key={g.key} style={{ backgroundColor: "#fafafa" }}>
                  <td>
                    <strong>{g.quantidadeDistribuidores}</strong>{" "}
                    {g.quantidadeDistribuidores === 1
                      ? "distribuidor"
                      : "distribuidores"}
                  </td>
                  <td>{g.tipo_divisao?.toUpperCase()}</td>
                  <td>
                    {moment(g.data_inicio).format("DD/MM/YY")} -{" "}
                    {moment(g.data_fim).format("DD/MM/YY")}
                  </td>
                  <td>{moment(g.created_at).format("DD/MM/YY HH:mm")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </WhiteBox>
      )}
    </>
  );
}

// ===== MANUAL CREDIT COMPONENT =====
function ManualCredit({ form, setForm, allUsers, loading, onSubmit, theme }) {
  return (
    <WhiteBox>
      <Title>Crédito/Débito Manual</Title>
      <Text style={{ color: "#999", fontSize: 12, marginBottom: 15 }}>
        Lançamento avulso de crédito ou débito para um distribuidor. O campo
        motivo é obrigatório.
      </Text>
      <Col2Grid style={{ gap: 10 }}>
        <div>
          <Label>Distribuidor</Label>
          <select
            value={form.id_user}
            onChange={(e) => setForm((s) => ({ ...s, id_user: e.target.value }))}
            style={{
              width: "100%",
              padding: 10,
              borderRadius: 4,
              border: "1px solid #ccc",
              marginBottom: 16,
            }}
          >
            <option value="">Selecione...</option>
            {allUsers.map((u) => (
              <option key={u.id} value={u.id}>
                {u.full_name || u.name} {u.cnpj ? `(${u.cnpj})` : ""}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label>Tipo</Label>
          <FH style={{ gap: 10, marginBottom: 16 }}>
            <Label
              style={{ cursor: "pointer", fontSize: 14 }}
              onClick={() => setForm((s) => ({ ...s, tipo: "credito" }))}
            >
              <FontAwesomeIcon
                icon={form.tipo === "credito" ? faCheckCircle : faCircle}
                style={{ color: form.tipo === "credito" ? "#4caf50" : "#ccc" }}
              />{" "}
              Crédito
            </Label>
            <Label
              style={{ cursor: "pointer", fontSize: 14 }}
              onClick={() => setForm((s) => ({ ...s, tipo: "debito" }))}
            >
              <FontAwesomeIcon
                icon={form.tipo === "debito" ? faCheckCircle : faCircle}
                style={{ color: form.tipo === "debito" ? "#d32f2f" : "#ccc" }}
              />{" "}
              Débito
            </Label>
          </FH>
        </div>
        <div>
          <Label>Valor (OrgaDots)</Label>
          <Input
            type="number"
            value={form.valor}
            onChange={(e) => setForm((s) => ({ ...s, valor: e.target.value }))}
            placeholder="Ex: 500"
          />
        </div>
        <div>
          <Label>Motivo / Descrição</Label>
          <Input
            value={form.descricao}
            onChange={(e) =>
              setForm((s) => ({ ...s, descricao: e.target.value }))
            }
            placeholder="Motivo obrigatório"
          />
        </div>
      </Col2Grid>
      <Button onClick={onSubmit} disabled={loading} style={{ marginTop: 10 }}>
        {loading ? <Spinner size="sm" /> : "Lançar"}
      </Button>
    </WhiteBox>
  );
}

// ===== EXTRATO CONSULTA COMPONENT =====
function ExtratoConsulta({
  allUsers,
  extratoUserId,
  setExtratoUserId,
  extrato,
  saldo,
  loading,
  onLoad,
  theme,
}) {
  return (
    <>
      <WhiteBox style={{ marginBottom: 20 }}>
        <Title>Consultar Extrato de Distribuidor</Title>
        <FH style={{ gap: 10, alignItems: "flex-end" }}>
          <div style={{ flex: 1 }}>
            <Label>Distribuidor</Label>
            <select
              value={extratoUserId}
              onChange={(e) => setExtratoUserId(e.target.value)}
              style={{
                width: "100%",
                padding: 10,
                borderRadius: 4,
                border: "1px solid #ccc",
              }}
            >
              <option value="">Selecione...</option>
              {allUsers.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.full_name || u.name} {u.cnpj ? `(${u.cnpj})` : ""}
                </option>
              ))}
            </select>
          </div>
          <Button
            style={{ width: "auto", marginBottom: 0 }}
            onClick={onLoad}
            disabled={loading}
          >
            {loading ? <Spinner size="sm" /> : "Consultar"}
          </Button>
        </FH>
      </WhiteBox>

      {saldo !== null && (
        <WhiteBox style={{ marginBottom: 20 }}>
          <FH style={{ gap: 10, alignItems: "center" }}>
            <Text style={{ color: "#999", fontSize: 12 }}>Saldo:</Text>
            <span
              style={{
                fontSize: 24,
                fontWeight: 700,
                color: theme.primary,
              }}
            >
              <FontAwesomeIcon icon={faCoin} />{" "}
              {parseFloat(saldo).toLocaleString("pt-BR", {
                minimumFractionDigits: 2,
              })}
            </span>
          </FH>
        </WhiteBox>
      )}

      {extrato.length > 0 && (
        <WhiteBox>
          <Title>Extrato</Title>
          <table
            className="table table-sm align-middle"
            style={{ fontSize: 13, marginTop: 10 }}
          >
            <thead>
              <tr>
                <th>Data</th>
                <th>Tipo</th>
                <th>Origem</th>
                <th>Descrição</th>
                <th style={{ textAlign: "right" }}>Valor</th>
              </tr>
            </thead>
            <tbody>
              {extrato.map((item) => (
                <tr key={item.id}>
                  <td>{moment(item.created_at).format("DD/MM/YY HH:mm")}</td>
                  <td
                    style={{
                      color:
                        item.tipo === "credito" ? "#00a751" : "#d32f2f",
                      fontWeight: 600,
                    }}
                  >
                    {item.tipo === "credito" ? "Crédito" : "Débito"}
                  </td>
                  <td>
                    {item.origem === "calculo_mensal"
                      ? "Cálculo Mensal"
                      : item.origem === "manual"
                      ? "Manual"
                      : item.origem === "compra"
                      ? "Compra"
                      : item.origem}
                  </td>
                  <td>{item.descricao || "-"}</td>
                  <td
                    style={{
                      textAlign: "right",
                      fontWeight: 600,
                      color:
                        item.tipo === "credito" ? "#00a751" : "#d32f2f",
                    }}
                  >
                    {item.tipo === "credito" ? "+" : "-"}
                    <FontAwesomeIcon icon={faCoin} style={{ marginRight: 4 }} />
                    {parseFloat(item.valor).toLocaleString("pt-BR", {
                      minimumFractionDigits: 2,
                    })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </WhiteBox>
      )}
    </>
  );
}

export default OrgaDots;
